import { Search, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { pdfjs } from 'react-pdf'

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// 検索結果の型
type SearchResultItem = {
  matchIndex: number
  pageIndex: number
  excerpt: string
  keyword: string
}

type PdfSearchSheetProps = {
  open: boolean
  onClose: () => void
  /** ビューアが読み込み済みの PDF。検索で再ダウンロードしないよう使い回す */
  pdfDoc: pdfjs.PDFDocumentProxy | null
  onKeywordChange: (keyword: string) => void
  onJumpToPage: (pageIndex: number) => void
  /** 選択中の検索結果が変わったとき (pageIndex, そのページ内での出現順) */
  onCurrentMatchChange?: (pageIndex: number, matchIndexInPage: number) => void
}

// 周辺テキストを抽出
const extractExcerpt = (pageText: string, startIndex: number, endIndex: number, contextLength: number = 40): string => {
  const beforeStart = Math.max(0, startIndex - contextLength)
  const afterEnd = Math.min(pageText.length, endIndex + contextLength)

  let excerpt = pageText.slice(beforeStart, afterEnd)
  if (beforeStart > 0) excerpt = '...' + excerpt
  if (afterEnd < pageText.length) excerpt = excerpt + '...'
  excerpt = excerpt.replace(/\n/g, ' ').replace(/\s+/g, ' ')
  return excerpt
}

// ハイライト付きで抜粋を表示
const highlightExcerpt = (excerpt: string, keyword: string) => {
  if (!keyword) return excerpt
  const parts: React.ReactNode[] = []
  const lowerExcerpt = excerpt.toLowerCase()
  const lowerKeyword = keyword.toLowerCase()
  let lastIndex = 0

  let index = lowerExcerpt.indexOf(lowerKeyword, lastIndex)
  while (index !== -1) {
    if (index > lastIndex) parts.push(excerpt.slice(lastIndex, index))
    parts.push(
      <mark key={index} className="bg-yellow-200 text-black px-0.5 rounded">
        {excerpt.slice(index, index + keyword.length)}
      </mark>,
    )
    lastIndex = index + keyword.length
    index = lowerExcerpt.indexOf(lowerKeyword, lastIndex)
  }
  if (lastIndex < excerpt.length) parts.push(excerpt.slice(lastIndex))
  return parts.length > 0 ? parts : excerpt
}

export const PdfSearchSheet = ({
  open,
  onClose,
  pdfDoc,
  onKeywordChange,
  onJumpToPage,
  onCurrentMatchChange,
}: PdfSearchSheetProps) => {
  const [keyword, setKeyword] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchedKeyword, setSearchedKeyword] = useState('')
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1)

  // 検索実行
  const handleSearch = useCallback(async () => {
    if (!keyword.trim()) {
      setSearchResults([])
      onKeywordChange('')
      return
    }
    if (!pdfDoc) return

    setIsSearching(true)
    setSearchedKeyword(keyword)
    onKeywordChange(keyword)

    try {
      const results: SearchResultItem[] = []
      const lowerKeyword = keyword.toLowerCase()
      let globalMatchIndex = 0

      for (let i = 0; i < pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i + 1) // pdfjs は 1-indexed
        const textContent = await page.getTextContent()
        const pageText = textContent.items.map((item) => ('str' in item ? item.str : '')).join(' ')
        const lowerPageText = pageText.toLowerCase()

        let pos = lowerPageText.indexOf(lowerKeyword)
        while (pos !== -1) {
          const endPos = pos + keyword.length
          results.push({
            matchIndex: globalMatchIndex++,
            pageIndex: i,
            excerpt: extractExcerpt(pageText, pos, endPos),
            keyword: pageText.slice(pos, endPos),
          })
          pos = lowerPageText.indexOf(lowerKeyword, pos + 1)
        }
      }

      setSearchResults(results)
    } catch (error) {
      console.error('検索エラー:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [keyword, pdfDoc, onKeywordChange])

  const handleClear = useCallback(() => {
    setKeyword('')
    setSearchResults([])
    setSearchedKeyword('')
    setCurrentMatchIndex(-1)
    onKeywordChange('')
  }, [onKeywordChange])

  const handleJumpToResult = useCallback(
    (result: SearchResultItem) => {
      setCurrentMatchIndex(result.matchIndex)
      onJumpToPage(result.pageIndex)
      // そのページ内で何番目のマッチかを計算
      const matchesOnSamePage = searchResults.filter((r) => r.pageIndex === result.pageIndex)
      const indexInPage = matchesOnSamePage.findIndex((r) => r.matchIndex === result.matchIndex)
      onCurrentMatchChange?.(result.pageIndex, indexInPage)
    },
    [onJumpToPage, onCurrentMatchChange, searchResults],
  )

  // 結果をページごとにグループ化
  const groupedResults = useMemo(() => {
    const resultMap = new Map<string, SearchResultItem[]>()
    for (const result of searchResults) {
      const pageKey = `ページ ${result.pageIndex + 1}`
      const existing = resultMap.get(pageKey) ?? []
      resultMap.set(pageKey, [...existing, result])
    }
    return Object.fromEntries(resultMap) as Record<string, SearchResultItem[]>
  }, [searchResults])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <BottomSheet
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
      title={searchResults.length > 0 ? `検索 (${searchResults.length}件)` : '検索'}
      snapPoints={[0.5, 1]}
      defaultSnapPoint={0.5}
      noOverlay
      dismissible={false}
    >
      <div className="flex flex-col h-full">
        {/* 検索入力エリア */}
        <div className="px-4 py-3 border-b">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="検索キーワードを入力..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch()
                }}
                className="pr-8"
                autoFocus={open}
              />
              {keyword && (
                <button
                  onClick={handleClear}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <Button onClick={handleSearch} disabled={isSearching || !keyword.trim() || !pdfDoc}>
              <Search size={18} />
            </Button>
          </div>
        </div>

        {/* 検索結果一覧 */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {isSearching ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">検索中...</div>
          ) : searchResults.length > 0 ? (
            <div>
              <div className="px-4 py-2 text-sm text-muted-foreground bg-muted border-b border-border">
                {searchResults.length}件の結果
              </div>
              {Object.entries(groupedResults).map(([pageKey, pageResults]) => (
                <div key={pageKey} className="relative">
                  <div className="sticky top-0 z-10 px-4 py-2 bg-secondary border-b border-border">
                    <span className="text-sm text-secondary-foreground font-medium">{pageKey}</span>
                    <span className="text-xs text-muted-foreground ml-2">({pageResults.length}件)</span>
                  </div>
                  <div className="divide-y divide-border">
                    {pageResults.map((result) => (
                      <button
                        key={result.matchIndex}
                        onClick={() => handleJumpToResult(result)}
                        className={cn(
                          'w-full text-left px-4 py-3 hover:bg-accent transition-colors',
                          'focus:outline-none focus:bg-accent',
                          currentMatchIndex === result.matchIndex && 'bg-accent/50',
                        )}
                      >
                        <p className="text-sm leading-relaxed text-foreground break-all">
                          {highlightExcerpt(result.excerpt, searchedKeyword)}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : searchedKeyword && !isSearching ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              結果が見つかりませんでした
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              キーワードを入力して検索してください
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  )
}
